import {
    ChangeDetectionStrategy,
    Component,
} from '@angular/core';
import { Setting } from '@app-state';
import { LICENSE_DEFINITIONS } from '@components/shared/license/license.constants';
import {
    globalStateFilter,
    IHostLicense,
    ITenant,
    ModalResultType,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import { FeatureflagsModalComponent } from '@pages/host/tenants/feature-flags/feature-flags.modal';
import { TenantLicenseModalComponent } from '@pages/host/tenants/license/tenant-license.modal';
import { TenantModalComponent } from '@pages/host/tenants/tenant.modal';
import {
    FeaturesService,
    ModalService,
    TenantService,
} from '@services';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    merge,
    Observable,
} from 'rxjs';
import {
    finalize,
    switchMap,
    tap,
} from 'rxjs/operators';

@Component({
    templateUrl: './tenants.page.html',
    styles: [`
        .card-container:first-child {
            padding-left: 0;
        }

        .card-container:last-child {
            padding-right: 0;
        }
    `],
    providers: [
        {
            provide: GRID_SERVICE,
            useClass: TenantService,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
        FeaturesService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantsPageComponent extends ODataGridComponent<ITenant, number>  {
    public license$ = this._appState.user.license.value$ as Observable<IHostLicense>;
    public loading$ = new BehaviorSubject(false);
    public statusList = globalStateFilter();
    public licenseDefinitions = LICENSE_DEFINITIONS;
    public isMultipleDatabasesEnabled = !!this._appState.settings.get(Setting.isMultipleDatabasesEnabled);

    private _tenantService = this._service as TenantService;
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _featuresService = this._injector.get<FeaturesService>(FeaturesService);

    public addTenant(tenant?: ITenant) {
        this._modalService
            .openModal(TenantModalComponent, cloneDeep(tenant))
            .afterClosedResult()
            .subscribe(res => {
                if (res.type === ModalResultType.Add) {
                    this._refresh$.next();
                }
            });
    }

    public allocateLicenses(tenant: ITenant) {
        const modalRef = this._modalService
            .openModal(
                TenantLicenseModalComponent,
                cloneDeep(tenant), {
                    data: {
                        readonly: true,
                        closeAfterAllocation: true,
                    },
                    size: 'sm',
                    additionalClasses: ['allocate-license-modal'],
                },
            );

        merge(
            modalRef.customEvent,
            modalRef.afterClosedResult(),
        )
            .subscribe((res: any) => {
                if (res && res.type === ModalResultType.Update) {
                    this._refresh$.next();
                }
            });
    }

    public setActive(active: boolean, tenantId?: number) {
        const tenantIds = tenantId ?
            [tenantId] :
            this._grid
                .selectionManager
                .selected
                .filter(e => e.isActive !== active)
                .map(e => e.id);

        if (tenantIds.length) {
            this._modalService.onConfirm(
                null,
                () => {
                    this.loading$.next(true);

                    this._tenantService.setActive(tenantIds, active)
                        .pipe(
                            finalize(() => {
                                this._refresh$.next();
                                this.loading$.next(false);
                            }),
                        )
                        .subscribe();
                },
                () => { },
                this._translate.instant(
                    active ?
                        'tenants_action_confirm_enable' :
                        'tenants_action_confirm_disable',
                ),
            );
        }
    }

    public get isSomeTenantActive(): boolean {
        return this._grid.selectionManager.selected.some(s => s.isActive);
    }

    public manageFeatureFlags(tenantId?: number) {
        const tenantIds = tenantId ?
            [tenantId] :
            this._grid
                .selectionManager
                .selected.map(e => e.id);
        this.loading$.next(true);
        this._featuresService.getFeatureFlagsForTenants(tenantIds)
            .pipe(
                tap(() => this.loading$.next(false)),
                switchMap((featureDisplayInfo) =>
                    this._modalService.openModal(FeatureflagsModalComponent, {
                        tenantIds,
                        features: featureDisplayInfo,
                    }).afterClosedResult()),
                finalize(() => {
                    this.loading$.next(false);
                }),
            )
            .subscribe(res => {
                if (res.type === ModalResultType.Update) {
                    this._refresh$.next();
                }
            });
    }

    public get isSomeTenantDisabled(): boolean {
        return this._grid.selectionManager.selected.some(s => !s.isActive);
    }
}
